import {
  Box,
  Button,
  Center,
  Flex,
  Image,
  Modal,
  ModalBody,
  ModalContent,
  ModalOverlay,
  Spinner,
  Stack,
  Text,
  VStack,
} from '@chakra-ui/react';
import type {
  VoucherEligibilityItem,
  VoucherGetByCodeResponse,
  VoucherGetEligibilitiesItem,
} from '@diamond/shared/types';
import { getErrorMessage } from '@diamond/shared/utils';
import {
  useCartTotal,
  useProductVoucherEligibilityList,
  useVoucherByCode,
  useVoucherCombinationRules,
  useVoucherStore,
} from '@diamond/sol-buyer-context';
import { InfoOutlined } from '@mui/icons-material';
import { useEffect, useState } from 'react';
import { useInView } from 'react-intersection-observer';

import VoucherDetailsContent from '../../carts/cart-voucher-modal/VoucherDetailsContent';
import VoucherListSection from '../../voucher/VoucherListSection';
import { ProductDetailVoucherHeader } from './ProductDetailVoucherHeader';

interface ProductDetailVoucherModalProps {
  isOpen: boolean;
  onClose: () => void;
  itemCode?: number;
}

type ViewMode = 'list' | 'details';

const mergeVoucherDetails = (
  summary: VoucherEligibilityItem | VoucherGetByCodeResponse,
  detail: VoucherGetByCodeResponse
): VoucherEligibilityItem | VoucherGetByCodeResponse => {
  const messageRules =
    'message_rule_products' in summary ? summary.message_rule_products : [];
  const ineligibilityReasons =
    'ineligibility_reasons' in summary
      ? summary.ineligibility_reasons
      : undefined;
  return {
    ...summary,
    ...detail,
    ...(messageRules ? { message_rule_products: messageRules } : {}),
    ...(ineligibilityReasons
      ? { ineligibility_reasons: ineligibilityReasons }
      : {}),
  };
};

const toEpochSeconds = (value: string): number => {
  const time = new Date(value).getTime();
  if (Number.isNaN(time) || time <= 0) {
    return 0;
  }

  return Math.floor(time / 1000);
};

const normalizeProductVoucherItem = (
  voucher: VoucherGetEligibilitiesItem
): VoucherEligibilityItem => {
  return {
    id: voucher.id,
    code: voucher.code,
    title: voucher.title,
    description: voucher.description,
    banner_url: voucher.banner_url,
    period_start: toEpochSeconds(voucher.period_start),
    period_end: toEpochSeconds(voucher.period_end),
    allow_other_vouchers: true,
    division: null,
    reward: {
      type: 'DISCOUNT_PERCENT',
      discount_percent: null,
      discount_amount: null,
      reward_rule_products: [],
      is_bundling: false,
      min_total_quantity_all_item: null,
    },
    usage: {
      remaining_global: null,
      remaining_per_customer: null,
    },
    is_eligible: true,
    message_rule_products: voucher.message_rule_products?.map(
      (messageRule) => ({
        message: messageRule.message,
        products: messageRule.products?.map((product) => ({
          product_item_code: product.product_item_code,
          product_name: product.product_name,
          min_quantity: product.min_quantity,
        })),
      })
    ),
    message_term_vouchers: voucher.message_term_vouchers,
  };
};

export const ProductDetailVoucherModal = ({
  isOpen,
  onClose,
  itemCode,
}: ProductDetailVoucherModalProps) => {
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [selectedVoucherForDetails, setSelectedVoucherForDetails] = useState<
    VoucherEligibilityItem | VoucherGetByCodeResponse | null
  >(null);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);
  const [detailFetchError, setDetailFetchError] = useState<string | null>(null);
  const [isSyncingCart, setIsSyncingCart] = useState(false);

  const isVoucherApplied = useVoucherStore((state) => state.isVoucherApplied);
  const isPendingVoucher = useVoucherStore((state) => state.isPendingVoucher);
  const clearPendingVouchers = useVoucherStore(
    (state) => state.clearPendingVouchers
  );

  const { isFetching: isCartTotalFetching } = useCartTotal();
  const {
    data: voucherList,
    isLoading: isLoadingListFromApi,
    isError: isErrorListFromApi,
    error: errorList,
    refetch: refetchList,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useProductVoucherEligibilityList({
    itemCode,
    enabled: isOpen,
  });
  const { ref: loadMoreTriggerRef, inView } = useInView();

  const { fetchWithCache } = useVoucherByCode();

  const appliedVouchers = useVoucherStore((state) => state.appliedVouchers);
  const appliedVoucherCodes = appliedVouchers.map((voucher) => voucher.code);

  const eligibilityVouchers: VoucherEligibilityItem[] =
    voucherList?.pages?.flatMap((page) =>
      (page.data ?? []).map(normalizeProductVoucherItem)
    ) ?? [];
  const isLoadingList = isLoadingListFromApi;
  const isErrorList = isErrorListFromApi;
  const shouldShowVoucherList =
    Boolean(itemCode) && !isLoadingList && !isErrorList;

  const { isVoucherDisabledByCombination, getCombinationDisabledReason } =
    useVoucherCombinationRules({
      eligibilityVouchers,
      selectedVoucherForDetails: selectedVoucherForDetails ?? undefined,
      appliedVoucherCodes,
      isVoucherApplied,
    });

  const handleCardClick = async (
    voucher: VoucherEligibilityItem | VoucherGetByCodeResponse
  ) => {
    setDetailFetchError(null);
    setIsLoadingDetails(true);

    try {
      const fullVoucherDetails = await fetchWithCache(voucher.code.trim());
      const enrichedVoucher = mergeVoucherDetails(voucher, fullVoucherDetails);
      setSelectedVoucherForDetails(enrichedVoucher);
      setViewMode('details');
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Gagal memuat detail voucher';
      setDetailFetchError(errorMessage);
      setSelectedVoucherForDetails(voucher);
      setViewMode('details');
    } finally {
      setIsLoadingDetails(false);
    }
  };

  const handleBackFromDetails = () => {
    setViewMode('list');
    setSelectedVoucherForDetails(null);
    setDetailFetchError(null);
  };

  const handleRetryDetailsFetch = async () => {
    if (!selectedVoucherForDetails) return;
    await handleCardClick(selectedVoucherForDetails);
  };
  useEffect(() => {
    if (!isCartTotalFetching) {
      clearPendingVouchers();
    }
  }, [isCartTotalFetching, clearPendingVouchers]);

  useEffect(() => {
    clearPendingVouchers();
  }, [appliedVouchers, clearPendingVouchers]);

  useEffect(() => {
    if (!isOpen || viewMode !== 'list') {
      return;
    }

    if (inView && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [
    fetchNextPage,
    hasNextPage,
    inView,
    isFetchingNextPage,
    isOpen,
    viewMode,
  ]);

  const handleClose = () => {
    setViewMode('list');
    setSelectedVoucherForDetails(null);
    setDetailFetchError(null);
    onClose();
  };

  const handleCartSyncStart = () => {
    setIsSyncingCart(true);
  };

  const handleCartSyncComplete = () => {
    setIsSyncingCart(false);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      size="lg"
      scrollBehavior="inside"
      isCentered
    >
      <ModalOverlay />
      <ModalContent borderRadius="8px" overflow="hidden" maxW="520px">
        <ModalBody p={0} position="relative">
          {isSyncingCart && (
            <Flex
              position="absolute"
              inset={0}
              bg="whiteAlpha.800"
              zIndex={10}
              justify="center"
              align="center"
              direction="column"
              gap={4}
            >
              <Spinner size="lg" color="#007AFF" />
              <Text fontSize="md" fontWeight="medium">
                Menambahkan Produk ke Keranjang
              </Text>
            </Flex>
          )}

          {viewMode === 'details' ? (
            selectedVoucherForDetails && (
              <VoucherDetailsContent
                voucher={selectedVoucherForDetails}
                isApplied={isVoucherApplied(selectedVoucherForDetails.code)}
                isProcessing={isPendingVoucher(selectedVoucherForDetails.code)}
                actionMode="add_to_cart"
                isEligible={
                  'is_eligible' in selectedVoucherForDetails
                    ? selectedVoucherForDetails.is_eligible
                    : true
                }
                isLoadingDetails={isLoadingDetails}
                detailFetchError={detailFetchError}
                onApply={() => undefined}
                onBack={handleBackFromDetails}
                onClose={handleClose}
                onRetry={handleRetryDetailsFetch}
                isDisabled={isVoucherDisabledByCombination(
                  selectedVoucherForDetails.code
                )}
                disabledReason={getCombinationDisabledReason(
                  selectedVoucherForDetails.code
                )}
              />
            )
          ) : (
            <>
              <ProductDetailVoucherHeader
                onBack={handleClose}
                onClose={handleClose}
              />

              <Box bg="#F7F7FB" px="16px" py="20px" minH="300px">
                {!itemCode && (
                  <Center py={8} px={4}>
                    <Text as="b" fontSize={{ base: 'md', md: 'lg' }}>
                      Voucher belum tersedia untuk produk ini
                    </Text>
                  </Center>
                )}
                {itemCode && isLoadingList && (
                  <Flex justify="center" py="40px">
                    <Spinner size="lg" color="#007AFF" />
                  </Flex>
                )}
                {itemCode && isErrorList && !isLoadingList && (
                  <Center py={8} px={4}>
                    <VStack spacing={4} textAlign="center">
                      <Image
                        src="/voucher/voucher_error.png"
                        alt="Error loading voucher"
                        objectFit="cover"
                        display="block"
                        marginX="auto"
                        height={{ base: '120px', md: '160px' }}
                        width={{ base: '120px', md: '160px' }}
                      />
                      <Stack spacing={1}>
                        <Text as="b" fontSize={{ base: 'md', md: 'lg' }}>
                          Gagal memuat voucher
                        </Text>
                        <Text
                          fontSize={{ base: 'sm', md: 'md' }}
                          color="gray.500"
                        >
                          {getErrorMessage(errorList)}
                        </Text>
                      </Stack>
                      <Button variant="solid" onClick={() => refetchList()}>
                        Coba lagi
                      </Button>
                    </VStack>
                  </Center>
                )}
                {shouldShowVoucherList && (
                  <>
                    <VoucherListSection
                      vouchers={eligibilityVouchers}
                      statusFilter="all"
                      searchTerm=""
                      onVoucherApplied={() => undefined}
                      cardActionMode="add_to_cart"
                      hideDisabledSection
                      onVoucherClick={handleCardClick}
                      onCartSyncStart={handleCartSyncStart}
                      onCartSyncComplete={handleCartSyncComplete}
                    />
                    <Box
                      ref={loadMoreTriggerRef}
                      h={hasNextPage || isFetchingNextPage ? '80px' : '1px'}
                      display={
                        hasNextPage || isFetchingNextPage ? 'flex' : 'none'
                      }
                      justifyContent="center"
                      alignItems="center"
                    >
                      {isFetchingNextPage && (
                        <Spinner size="md" color="#007AFF" />
                      )}
                    </Box>
                  </>
                )}
              </Box>
            </>
          )}
        </ModalBody>
        {viewMode === 'list' && (
          <Box px="16px" py="12px" bg="white" borderTop="1px solid" borderColor="gray.100">
            <Flex
              align="center"
              gap="8px"
              bg="#F0F6FF"
              border="1px solid"
              borderColor="#C7DDFF"
              borderRadius="8px"
              px="12px"
              py="10px"
            >
              <InfoOutlined sx={{ fontSize: 16, color: '#007AFF', flexShrink: 0 }} />
              <Text fontSize="sm" color="#444444">
                Aktivasi voucher di halaman Keranjang atau Checkout.
              </Text>
            </Flex>
          </Box>
        )}
      </ModalContent>
    </Modal>
  );
};

export default ProductDetailVoucherModal;
